import { IdentityUserAri } from '@atlassian/ari';

/**
 * Note that we can't sort by entity.lastUpdated as this field is missing for some entities (Projects + Goals partially, Pull Requests entirely)
 * If the field is null, Cypher brings those results to the top, which is a random mismash of old relationships (think 2024 PRs)
 *
 * useHomeAssignedCypherQuery will try and use entity.lastUpdated if it exists for manual sorting, but will fallback to rel.createdAt
 *
 * Defensively we always sort by rel.createdAt for now as it'll always exist.
 */

const TOWNSQUARE_RELATIONSHIPS = ['atlas_goal_has_owner', 'atlas_project_has_owner'];
const generateTownsquareCypherQuery = (accountId: string): string => {
	const userAri = IdentityUserAri.create({ userId: accountId }).toString();

	// Note that unlike the other queries, we can't filter on project / goal state.
	// The entities don't have the info yet. We're FE filtering out invalid entities instead.
	return (
		`MATCH (user:IdentityUser {ari: '${userAri}'})<-[rel:${TOWNSQUARE_RELATIONSHIPS.join('|')}]-(townsquareEntity) ` +
		`RETURN townsquareEntity, townsquareEntity.lastUpdated, rel.createdAt ` +
		`ORDER BY rel.createdAt DESC`
	);
};

const JIRA_ISSUE_RELATIONSHIPS = ['user_assigned_issue'];
const generateJiraIssueCypherQuery = (accountId: string): string => {
	const userAri = IdentityUserAri.create({ userId: accountId }).toString();

	return (
		`MATCH (user:IdentityUser {ari: '${userAri}'})-[rel:${JIRA_ISSUE_RELATIONSHIPS.join('|')}]->(issue:JiraIssue) ` +
		`WHERE issue.statusCategory <> 'done' ` +
		`RETURN issue, issue.lastUpdated, rel.createdAt ` +
		`ORDER BY rel.createdAt DESC`
	);
};

const PR_RELATIONSHIPS = ['user_reviews_pr', 'user_authored_pr'];
const generatePullRequestCypherQuery = (accountId: string): string => {
	const userAri = IdentityUserAri.create({ userId: accountId }).toString();

	const cypherQuery =
		`MATCH (user:IdentityUser {ari: '${userAri}'})-[rel_3p:user_linked_third_party_user]->(thirdPartyUser:IdentityThirdPartyUser) ` +
		// Filtering for bitbucket PRs exclusively for now. Need permission to show 3P
		`WHERE rel_3p.integrationAri = 'ari:cloud:platform::integration/bitbucket' ` +
		`MATCH (thirdPartyUser)-[rel:${PR_RELATIONSHIPS.join('|')}]->(pr:ExternalPullRequest) ` +
		`WHERE pr.status IN ['OPEN', 'DRAFT'] ` +
		`RETURN pr, pr.lastUpdated, rel.createdAt ` +
		`ORDER BY rel.createdAt DESC`;

	return cypherQuery;
};

export const createTownsquareCypherQuery = (accountId: string): string =>
	generateTownsquareCypherQuery(accountId);

export const createJiraIssueCypherQuery = (accountId: string): string =>
	generateJiraIssueCypherQuery(accountId);

export const createPullRequestCypherQuery = (accountId: string): string =>
	generatePullRequestCypherQuery(accountId);
